import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { memo, useMemo, useRef } from 'react';
import { Platform, View, ViewStyle } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';

const kHandle = {
  alignSelf: 'center',
  width: 64,
  height: 18,
  alignItems: 'center',
  justifyContent: 'center',
  // On the web the pointer says resize; a touch reads the grabber.
  cursor: 'ns-resize',
} as ViewStyle;

/** A touch holds before it resizes, so a flick on the grabber scrolls. */
const kHoldMs = 250;

/**
 * The grabber in a phone card's footer, under its content: dragging it up or down
 * changes the card's height in the one-column view only. On a touch screen
 * the drag starts after a short hold; the dashboard's gesture-handler
 * scroll view then yields to it.
 */
export const NarrowResizeHandle = memo(
  ({
    title,
    onMove,
    onEnd,
    onCancel,
  }: {
    title: string;
    /** The pointer moved by `dy` since the drag began. */
    onMove: (dy: number) => void;
    onEnd: (dy: number) => void;
    onCancel: () => void;
  }) => {
    // The latest callbacks, read by one gesture: a gesture rebuilt while a
    // drag re-renders the card would cancel the drag.
    const latest = useRef({ onMove, onEnd, onCancel });
    latest.current = { onMove, onEnd, onCancel };
    const resize = useMemo(() => {
      const pan = Gesture.Pan()
        .runOnJS(true)
        .onUpdate(event => latest.current.onMove(event.translationY))
        .onEnd((event, success) =>
          success
            ? latest.current.onEnd(event.translationY)
            : latest.current.onCancel(),
        );
      return Platform.OS === 'web'
        ? pan.minDistance(3)
        : pan.activateAfterLongPress(kHoldMs);
    }, []);
    return (
      <GestureDetector gesture={resize}>
        <View
          accessibilityLabel={`${title} 的高度`}
          accessibilityHint="按住后上下拖动，调整这张卡片在手机上的高度"
          style={kHandle}
        >
          <View
            style={{
              width: 36,
              height: 4,
              borderRadius: 2,
              backgroundColor: aiColors.border,
            }}
          />
        </View>
      </GestureDetector>
    );
  },
);
